/**
 * The locale model behind the SPA's localization (ADR-031): the languages Planlets ships in,
 * their autonyms for the picker (never translated), the Intl tag each one formats dates with,
 * and how a list of language tags — the user's stored choice, `navigator.languages`, a shell's
 * per-app language — resolves to one of them. English is the default and the fallback. Pure
 * and DOM-free: part of the headless core (ADR-029 §2), so every install resolves the same way.
 */

export type Locale = 'en' | 'nb' | 'nn';

/** Every supported locale, in picker order. */
export const LOCALES: readonly Locale[] = ['en', 'nb', 'nn'];

/** Each language's own name — what the picker shows, identical in every locale. */
export const LOCALE_NAMES: Record<Locale, string> = {
  en: 'English',
  nb: 'Norsk bokmål',
  nn: 'Norsk nynorsk',
};

const INTL_TAGS: Record<Locale, string | undefined> = {
  en: undefined,
  nb: 'nb-NO',
  nn: 'nn-NO',
};

/**
 * The BCP 47 tag `Intl` formats dates and numbers with for a locale — the Norwegian region tags
 * for nb and nn, and `undefined` for English: English has regional conventions the SPA does not
 * pick between (en-GB, en-US, en-IN …), and `undefined` hands `Intl` the host's own default,
 * exactly what an English user saw before localization.
 */
export function intlTagFor(locale: Locale): string | undefined {
  return INTL_TAGS[locale];
}

/** Whether an unknown value — a stored setting, a query — names a supported locale. */
export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/**
 * The locale a preference-ordered list of language tags asks for: the first tag whose primary
 * subtag (lower-cased, before any `-` or `_`) we ship — `nn` is Nynorsk; `nb` and the macro
 * language `no` are Bokmål; `en` is English — or English when none of them is.
 */
export function resolveLocale(tags: readonly string[]): Locale {
  for (const tag of tags) {
    switch (tag.toLowerCase().split(/[-_]/)[0]) {
      case 'nn':
        return 'nn';
      case 'nb':
      case 'no':
        return 'nb';
      case 'en':
        return 'en';
    }
  }
  return 'en';
}
